import { AsyncPipe, CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NotificationsService } from '@app/core/notifications/services/notifications.service';
import { PricePipe } from '@app/core/pipes/price.pipe';
import { IsFavoriteComponent } from '@app/entities/images/components/is-favorite/is-favorite.component';
import { Image } from '@app/entities/images/interfaces/image';
import { ImageDetail } from '@app/entities/images/interfaces/image-details';
import { ToggleSwitchComponent } from '@app/layout/toggle-switch/toggle-switch.component';
import {
  AddToCart,
  AuthSelectors,
  CartSelectors,
  ImageSelectors,
  RemoveFromCart,
} from '@app/store';
import { Store } from '@ngxs/store';
import { filter, map, Observable, of, take, tap } from 'rxjs';

@Component({
  selector: 'app-purchase-image',
  templateUrl: './purchase-image.component.html',
  styleUrls: ['./purchase-image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    AsyncPipe,
    PricePipe,
    IsFavoriteComponent,
    ReactiveFormsModule,
    ToggleSwitchComponent,
  ],
  providers: [CurrencyPipe],
})
export class PurchaseImageComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private destroyRef: DestroyRef,
    private notificationsService: NotificationsService,
  ) {}

  protected isAuthenticated$: Observable<boolean> = this.store.select(
    AuthSelectors.isAuthenticated,
  );

  protected imageId$: Observable<number> = inject(Store).select(
    ImageSelectors.imageId,
  ) as Observable<number>;

  protected imageName$: Observable<string> = inject(Store).select(
    ImageSelectors.imageName,
  ) as Observable<string>;

  protected image$: Observable<Image | undefined> = inject(Store).select(
    ImageSelectors.image,
  ) as Observable<Image>;

  protected imageDetails$: Observable<ImageDetail[] | undefined> = inject(
    Store,
  ).select(ImageSelectors.imageDetails);

  protected isFavorite$: Observable<boolean> = inject(Store).select(
    ImageSelectors.isFavorite,
  ) as Observable<boolean>;

  protected imageDetailsIds$: Observable<number[]> = inject(Store).select(
    CartSelectors.imageDetailsIds,
  );

  protected form!: FormGroup;

  ngOnInit(): void {
    this.form = this.buildForm();
    this.populateForm();
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      detail_id: [null, Validators.required],
    });
  }

  private populateForm(): void {
    this.imageDetails$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((details: ImageDetail[] | undefined) => {
          return details !== undefined;
        }),
        take(1),
        tap((details: ImageDetail[]) => {
          const filteredDetails = details.filter((detail: ImageDetail) => {
            return !detail.url;
          });

          const defaultDetailId =
            filteredDetails && filteredDetails.length > 0
              ? filteredDetails[0].id
              : null;

          this.detailIdFC.setValue(defaultDetailId);
        }),
      )
      .subscribe();
  }

  get detailIdFC(): FormControl {
    return this.form.get('detail_id') as FormControl;
  }

  protected containsAsync(
    asyncArray: Observable<number[]>,
    item: number | null,
  ): Observable<boolean> {
    if (!item) {
      return of(false);
    }

    return asyncArray.pipe(
      map((items: number[]) => {
        return items.includes(item);
      }),
    );
  }
  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this.imageDetailsIds$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        tap((imageDetailsIds: number[]) => {
          if (imageDetailsIds.includes(this.detailIdFC.value)) {
            this.store.dispatch(new RemoveFromCart(this.detailIdFC.value));
          } else {
            this.store.dispatch(new AddToCart(this.detailIdFC.value));
            this.image$
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                take(1),
                tap((image: Image | undefined) => {
                  this.notificationsService.displaySuccessNotification(
                    'Successfully added',
                    'The selected image has been successfully added to the shopping cart.',
                    image,
                  );
                }),
              )
              .subscribe();
          }
        }),
      )
      .subscribe();
  }

  onClick(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const downloadLink = target.querySelector(
      '.download-link',
    ) as HTMLAnchorElement;

    if (downloadLink) {
      downloadLink.click();
      event.stopPropagation(); // Prevents any unintended bubbling
    }
  }
}
